import React from 'react'
// libs
import { useMsal } from '@azure/msal-react'
import { useRouter } from 'hooks/useRouter'
import { useSnackbar } from 'notistack'
import { useDispatch } from 'react-redux'
import { useMutation } from '@apollo/client/react'
import { setAccessToken } from 'lib/auth'
import { loginRequest } from 'providers/MicrosoftProvider/config'
// graphql
import { MICROSOFT_GET_COMPANIES, MICROSOFT_SIGN_IN_MUTATION } from 'graphql/auth/mutations'
// hooks
import { useShowModal } from 'hooks'
// store
import { setCurrentUser } from 'store/reducers/currentUser'
// constants
import { Modals } from 'constants/modals'
// types
import type { AccountInfo } from '@azure/msal-browser'
import { EventMessage, EventType } from '@azure/msal-browser'
import { IAuthMicrosoftTokenGetAvailableCompaniesResponse, IAuthUserWithCredentialsResponse } from 'types/graphql'
// utils
import { getSnackbarCloseButton } from 'providers/SnackbarProvider/utils'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'
import { clearRedirectUrl, getRedirectUrl } from 'utils/cookies'

type MicrosoftTokenGetAvailableCompanies = {
  authMicrosoftTokenGetAvailableCompanies: IAuthMicrosoftTokenGetAvailableCompaniesResponse
}

type ExchangeMicrosoftToken = {
  authExchangeMicrosoftToken: IAuthUserWithCredentialsResponse
}

export const useMicrosoftLogin = () => {
  const router = useRouter()
  const dispatch = useDispatch()

  const { instance } = useMsal()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const [microsoftSignInMt] = useMutation<ExchangeMicrosoftToken>(MICROSOFT_SIGN_IN_MUTATION)
  const [microsoftGetCompanies] = useMutation<MicrosoftTokenGetAvailableCompanies>(MICROSOFT_GET_COMPANIES)

  const { showModal } = useShowModal({ modalType: Modals.AUTH_COMPANY_SELECT_MODAL })

  const signIn = async (idToken: string) => {
    try {
      enqueueSnackbar('Logging into your account', {
        key: 'login',
        variant: 'info',
        autoHideDuration: null
      })

      const { data: companiesData } = await microsoftGetCompanies({
        variables: {
          token: idToken
        }
      })

      const companies = companiesData.authMicrosoftTokenGetAvailableCompanies.data || []

      if (!companies.length) {
        enqueueSnackbar('User or company details not found, please contact your admin.', {
          variant: 'warning',
          action: (key) => getSnackbarCloseButton(key, closeSnackbar)
        })

        return
      }

      let companyId = companies[0].id

      if (companies.length > 1) {
        companyId = await new Promise((resolve) => {
          showModal({ resolve, companies })
        })
      }

      const {
        data: {
          authExchangeMicrosoftToken: { data }
        }
      } = await microsoftSignInMt({
        variables: { token: idToken, companyId }
      })

      dispatch(setCurrentUser(data.user))
      setAccessToken(data.accessToken)

      const redirectUrl = getRedirectUrl()

      if (redirectUrl) {
        clearRedirectUrl()

        await router.push(redirectUrl)
      } else {
        await router.push('/home')
      }
    } catch (errors) {
      enqueueSnackbar(parseGraphQLErrorMessages(errors), {
        variant: 'warning',
        action: (key) => getSnackbarCloseButton(key, closeSnackbar)
      })
    } finally {
      closeSnackbar('login')
    }
  }

  const microsoftSignIn = async () => {
    instance.loginRedirect(loginRequest).catch((e) => {
      console.log('[microsoftSignIn.loginRedirect]', e)
    })
  }

  const onLoginSuccess = async (event: EventMessage) => {
    if (
      (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
      event.payload
    ) {
      await signIn((event.payload as AccountInfo)?.idToken)
    }
  }

  React.useEffect(() => {
    const callbackId = instance.addEventCallback(onLoginSuccess)

    return () => {
      instance.removeEventCallback(callbackId)
    }
  }, [])

  return {
    microsoftSignIn
  }
}
